<template>
    <div v-if="navigationData.length > 0" class="mega-nav" role="navigation" aria-label="Main">
        <ul class="mega-nav__items">
            <li
                v-for="(item, index) in navigationData"
                :key="item.id"
                :ref="el => setItemRef(el, index)"
                :class="[
                    'mega-nav__item',
                    { 'mega-nav__item--has-dropdown': hasSubNav(item), 'mega-nav__item--open': isMenuOpen(item, index) }
                ]"
                @mouseenter="handleItemMouseEnter(item, index)"
                @mouseleave="handleItemMouseLeave(item, index)"
                @focusin="handleItemFocusIn(item, index)"
                @focusout="handleItemFocusOut(item, index)"
            >
                <template v-if="hasSubNav(item)">
                    <!-- Real link: clicking navigates; the dropdown still opens on hover/focus -->
                    <a
                        v-if="hasRealUrl(item)"
                        class="mega-nav__link mega-nav__trigger"
                        :class="{ 'mega-nav__link--active': isCurrentUrl(item.url) }"
                        :href="item.url"
                        :target="item.target"
                        :ref="el => setTopLevelRef(el, index)"
                        :aria-expanded="isMenuOpen(item, index)"
                        :aria-controls="getMenuId(item, index)"
                        @keydown="onTopLevelKeydown($event, item, index)"
                    >
                        {{ item.name }}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
                        </svg>
                    </a>
                    <!-- Placeholder link ("#"): dropdown toggle only, not clickable through -->
                    <button
                        v-else
                        type="button"
                        class="mega-nav__link mega-nav__trigger"
                        :ref="el => setTopLevelRef(el, index)"
                        :aria-expanded="isMenuOpen(item, index)"
                        :aria-controls="getMenuId(item, index)"
                        @click.prevent="toggleMenu(item, index)"
                        @keydown="onTopLevelKeydown($event, item, index)"
                    >
                        {{ item.name }}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
                        </svg>
                    </button>
                </template>
                <template v-else>
                    <a
                        class="mega-nav__link"
                        :class="{ 'mega-nav__link--active': isCurrentUrl(item.url) }"
                        :href="item.url"
                        :target="item.target"
                        :ref="el => setTopLevelRef(el, index)"
                        @keydown="onTopLevelKeydown($event, item, index)"
                    >
                        {{ item.name }}
                    </a>
                </template>

                <template v-if="hasSubNav(item) && item.subNavigation.length > 1">
                    <div
                        :id="getMenuId(item, index)"
                        class="mega-nav__sub-nav-wrapper mega-nav__sub-nav-wrapper--mega"
                        @mouseenter="cancelCloseTimer"
                        @mouseleave="handleItemMouseLeave(item, index)"
                        @keydown.esc.prevent="closeMenu(index)"
                    >
                        <div class="container">
                            <div :class="['mega-nav__sub-nav', item.subNavigation.length > 1 ? 'mega-nav__sub-nav--columns' : '']" :style="[item.subNavigation.length > 1 ? '--mega-menu-columns:' + item.subNavigation.length : '']">
                                <ul v-for="(subNav, index) in item.subNavigation" :key="index" class="mega-nav__sub-items">
                                    <li v-if="subNav.title" class="mega-nav__sub-label">
                                        <template v-if="subNav.feature">
                                            <a :href="subNav.feature.url" :target="subNav.feature.target">
                                                {{ subNav.title }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            {{ subNav.title }}
                                        </template>
                                    </li>
                                    <li v-if="subNav.image" class="mega-nav__sub-image">
                                        <a v-if="subNav.feature" :href="subNav.feature.url" :target="subNav.feature.target">
                                            <img :width="subNav.image.width" :height="subNav.image.height" :src="subNav.image.url" :alt="subNav.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                        </a>
                                        <template v-else>
                                            <img :width="subNav.image.width" :height="subNav.image.height" :src="subNav.image.url" :alt="subNav.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                        </template>
                                    </li>
                                    <li v-for="subItem of subNav.items" class="mega-nav__sub-item"  :key="subItem.id">
                                        <a :class="['mega-nav__sub-link', { active: isCurrentUrl(subItem.url) }]" :href="subItem.url" :target="subItem.target">
                                            {{ subItem.name }}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else-if="hasSubNav(item) && item.subNavigation.length <= 1">
                    <div
                        :id="getMenuId(item, index)"
                        class="mega-nav__sub-nav-wrapper mega-nav__sub-nav-wrapper--dropdown"
                        @mouseenter="cancelCloseTimer"
                        @mouseleave="handleItemMouseLeave(item, index)"
                        @keydown.esc.prevent="closeMenu(index)"
                    >
                        <div class="mega-nav__sub-nav mega-nav__sub-nav--list">
                            <ul v-for="(subNav, index) in item.subNavigation" :key="index" class="mega-nav__sub-items">
                                <li v-if="subNav.title" class="mega-nav__sub-label">
                                    <a v-if="subNav.feature" :href="subNav.feature.url" :target="subNav.feature.target" @mouseenter="updateActiveSubItem(subNav.feature, subNav.image)">
                                        {{ subNav.title }}
                                    </a>
                                    <template v-else>
                                        {{ subNav.title }}
                                    </template>
                                </li>
                                <li v-for="subItem of subNav.items" class="mega-nav__sub-item" :key="subItem.id">
                                    <a :class="['mega-nav__sub-link', { active: isCurrentUrl(subItem.url) }]" :href="subItem.url" :target="subItem.target" @mouseenter="updateActiveSubItem(subItem, null)">
                                        {{ subItem.name }}
                                    </a>
                                </li>
                                <li v-if="subNav.feature" class="mega-nav__sub-item mega-nav__sub-item--feature">
                                    <a :class="['mega-nav__sub-link', { active: isCurrentUrl(subNav.feature.url) }]" :href="subNav.feature.url" :target="subNav.feature.target" @mouseenter="updateActiveSubItem(subNav.feature, subNav.image)">
                                        <span>{{ subNav.feature.name }}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20">
                                            <path d="M262.771-210.001 460.719-480 262.771-749.999h61.742L522.461-480 324.513-210.001h-61.742Zm241.692 0L702.411-480 504.463-749.999h61.742L764.153-480 566.205-210.001h-61.742Z"/>
                                        </svg>
                                    </a>
                                </li>
                            </ul>
                            <ul class="mega-nav__sub-items" v-if="activeSubItem && activeSubItem.image">
                                <li class="mega-nav__sub-image">
                                    <a :href="activeSubItem.url" :target="activeSubItem.target" :title="activeSubItem.name">
                                        <img :width="activeSubItem.image.width" :src="activeSubItem.image.url" :alt="activeSubItem.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                    </a>
                                    <a class="mega-nav__sub-link" :href="activeSubItem.url" :target="activeSubItem.target">
                                        <p class="text-small">{{ activeSubItem.name }}</p>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </template>
            </li>
        </ul>
    </div>
</template>

<script>

export default {
    name: 'MegaNavigation',
    data() {
        return {
            subNavigation: [],
            activeSubItem: null,
            openMenuKey: null,
            openTimer: null,
            closeTimer: null,
            itemRefs: [],
            topLevelRefs: []
        };
    },
    props: {
        navigationData: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    methods: {
        // Placeholder URLs ("#", javascript:void(0)) mean "dropdown only" —
        // the top level renders as a non-navigating button for those
        hasRealUrl(item) {
            const url = (item.url || '').trim();
            return url !== '' && url !== '#' && !url.toLowerCase().startsWith('javascript:');
        },
        getMenuKey(item, index) {
            return item.id ?? `menu-${index}`;
        },
        getMenuId(item, index) {
            return `mega-nav-menu-${this.getMenuKey(item, index)}`;
        },
        isMenuOpen(item, index) {
            return this.openMenuKey === this.getMenuKey(item, index);
        },
        setItemRef(el, index) {
            this.itemRefs[index] = el || null;
        },
        setTopLevelRef(el, index) {
            this.topLevelRefs[index] = el || null;
        },
        clearOpenTimer() {
            if (this.openTimer) {
                clearTimeout(this.openTimer);
                this.openTimer = null;
            }
        },
        cancelCloseTimer() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
        openMenu(item, index, { delayed = false } = {}) {
            if (!this.hasSubNav(item)) return;

            const key = this.getMenuKey(item, index);
            this.cancelCloseTimer();
            this.clearOpenTimer();

            if (delayed) {
                this.openTimer = setTimeout(() => {
                    this.openMenuKey = key;
                    this.openTimer = null;
                }, 90);
                return;
            }

            this.openMenuKey = key;
        },
        closeMenu(index = null) {
            if (index === null) {
                this.openMenuKey = null;
                this.resetActiveSubItem();
                return;
            }

            const item = this.navigationData[index];
            if (!item) return;

            const key = this.getMenuKey(item, index);
            if (this.openMenuKey === key) {
                this.openMenuKey = null;
                this.resetActiveSubItem();
            }
        },
        scheduleClose(item, index) {
            if (!this.hasSubNav(item)) return;

            const key = this.getMenuKey(item, index);
            this.clearOpenTimer();
            this.cancelCloseTimer();

            this.closeTimer = setTimeout(() => {
                if (this.openMenuKey === key) {
                    this.closeMenu(index);
                }
                this.closeTimer = null;
            }, 260);
        },
        toggleMenu(item, index) {
            if (!this.hasSubNav(item)) return;

            if (this.isMenuOpen(item, index)) {
                this.closeMenu(index);
                return;
            }

            this.updateActiveSubItem(this.getFirstSubNavItem(item), null);
            this.openMenu(item, index);
        },
        handleItemMouseEnter(item, index) {
            if (!this.hasSubNav(item)) return;

            this.cancelCloseTimer();
            this.updateActiveSubItem(this.getFirstSubNavItem(item), null);
            this.openMenu(item, index, { delayed: true });
        },
        handleItemMouseLeave(item, index) {
            this.scheduleClose(item, index);
        },
        handleItemFocusIn(item, index) {
            if (!this.hasSubNav(item)) return;
            this.cancelCloseTimer();
            this.updateActiveSubItem(this.getFirstSubNavItem(item), null);
            this.openMenu(item, index);
        },
        handleItemFocusOut(item, index) {
            if (!this.hasSubNav(item)) return;

            setTimeout(() => {
                const li = this.itemRefs[index];
                const focused = document.activeElement;

                if (!li || !focused || !li.contains(focused)) {
                    this.scheduleClose(item, index);
                }
            }, 0);
        },
        focusTopLevelByIndex(index) {
            const total = this.navigationData.length;
            if (!total) return;

            let cursor = (index + total) % total;
            let attempts = 0;

            while (attempts < total) {
                const el = this.topLevelRefs[cursor];
                if (el) {
                    el.focus();
                    return;
                }
                cursor = (cursor + 1) % total;
                attempts++;
            }
        },
        focusSubLink(index, first = true) {
            this.$nextTick(() => {
                const li = this.itemRefs[index];
                if (!li) return;

                const focusable = li.querySelectorAll('.mega-nav__sub-nav-wrapper a, .mega-nav__sub-nav-wrapper button, .mega-nav__sub-nav-wrapper [tabindex]:not([tabindex="-1"])');
                if (!focusable.length) return;

                const target = first ? focusable[0] : focusable[focusable.length - 1];
                target.focus();
            });
        },
        onTopLevelKeydown(event, item, index) {
            const key = event.key;

            if (key === 'ArrowRight') {
                event.preventDefault();
                this.focusTopLevelByIndex(index + 1);
                return;
            }

            if (key === 'ArrowLeft') {
                event.preventDefault();
                this.focusTopLevelByIndex(index - 1);
                return;
            }

            if (key === 'Escape') {
                event.preventDefault();
                this.closeMenu();
                return;
            }

            if (!this.hasSubNav(item)) return;

            if (key === 'Enter' || key === ' ') {
                event.preventDefault();
                this.toggleMenu(item, index);
                return;
            }

            if (key === 'ArrowDown') {
                event.preventDefault();
                this.updateActiveSubItem(this.getFirstSubNavItem(item), null);
                this.openMenu(item, index);
                this.focusSubLink(index, true);
                return;
            }

            if (key === 'ArrowUp') {
                event.preventDefault();
                this.updateActiveSubItem(this.getFirstSubNavItem(item), null);
                this.openMenu(item, index);
                this.focusSubLink(index, false);
            }
        },
        handleGlobalKeydown(event) {
            if (event.key !== 'Escape' || this.openMenuKey === null) return;
            this.closeMenu();
        },
        isCurrentUrl(url) {
            if (!url || url === '#') return false;

            try {
                const linkPath = new URL(url, window.location.origin).pathname.replace(/\/+$/, '') || '/';
                const currentPath = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
                return linkPath === currentPath;
            } catch {
                return false;
            }
        },
        getFirstSubNavItem(item) {
            if (item.subNavigation && item.subNavigation.length > 0) {
                const subNav = item.subNavigation[0];

                if (subNav && subNav.items.length > 0) {
                    return subNav.items[0];
                }
            }
            return null;
        },
        updateActiveSubItem(subItem, subImage) {
            if (!subItem) {
                this.activeSubItem = null;
                return;
            }

            if (subImage !== null) {
                subItem.image = subImage;
            }
            this.activeSubItem = { ...subItem };
        },
        resetActiveSubItem() {
            this.activeSubItem = null;
        },
        hasSubNav(item) {
            return item.subNavigation && item.subNavigation.length > 0;
        }
    },
    mounted() {
        window.addEventListener('keydown', this.handleGlobalKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleGlobalKeydown);
        this.clearOpenTimer();
        this.cancelCloseTimer();
    }
}
</script>
